import { useEffect, useState } from 'react';
import { debounce } from 'lodash';

/**
 * Debounces any value using lodash `debounce`, delaying state updates until
 * the value has been stable for the specified `delay`.
 *
 * Unlike {@link useDebounce} (which uses `setTimeout`), this implementation
 * uses lodash debounce and properly cancels the pending call on cleanup.
 *
 * @template T - The type of the value to debounce.
 * @param value - The input value to debounce.
 * @param delay - Debounce delay in milliseconds.
 * @returns The debounced value.
 *
 * @example
 * const debouncedQuery = useDebouncedValue(searchInput, 400);
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = debounce(() => setDebouncedValue(value), delay);

		handler();

		return () => {
			handler.cancel();
		};
	}, [value, delay]);

	return debouncedValue;
}

/**
 * Creates a debounced version of a function that delays invocation until
 * `wait` milliseconds have elapsed since the last call.
 *
 * @template T - The type of the function to debounce.
 * @param func - The function to wrap.
 * @param wait - The debounce wait time in milliseconds.
 * @returns A debounced wrapper of `func`.
 *
 * @example
 * const debouncedSave = debounceFunction(saveData, 500);
 */
export function debounceFunction<T extends (...args: any[]) => void>(
	func: T,
	wait: number
): (...args: Parameters<T>) => void {
	let timeout: ReturnType<typeof setTimeout>;

	return function executedFunction(...args: Parameters<T>) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
}
